import React, { useState } from 'react';
import { Share2, Linkedin, Twitter, Facebook, Link, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const SocialShare = ({ 
  title, 
  description, 
  url, 
  hashtags = ['Ikigai', 'Vocation', 'CareerPath'],
  language = 'fr' 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const hashtagString = hashtags.join(',');

  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}%0A%0A${encodedDescription}&url=${encodedUrl}&hashtags=${hashtagString}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedDescription}`
  };

  const handleShare = async (platform) => {
    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: shareUrl
        });
        toast.success(language === 'fr' ? 'Partagé avec succès !' : 'Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
        }
      }
    } else if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(`${title}\n\n${description}\n\n${shareUrl}`);
        setCopied(true);
        toast.success(language === 'fr' ? 'Copié dans le presse-papier !' : 'Copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error(language === 'fr' ? 'Erreur de copie' : 'Copy failed');
      }
    } else {
      window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    }
    setShowModal(false);
  };

  const socialButtons = [
    { 
      id: 'linkedin', 
      icon: Linkedin, 
      label: 'LinkedIn', 
      color: '#0077B5',
      bgColor: '#0077B515'
    },
    { 
      id: 'twitter', 
      icon: Twitter, 
      label: 'Twitter / X', 
      color: '#1DA1F2',
      bgColor: '#1DA1F215'
    },
    { 
      id: 'facebook', 
      icon: Facebook, 
      label: 'Facebook', 
      color: '#1877F2',
      bgColor: '#1877F215'
    }
  ];

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant="outline"
        className="flex items-center gap-2 rounded-xl"
        data-testid="share-btn"
      >
        <Share2 className="w-4 h-4" />
        <span className="hidden sm:inline">
          {language === 'fr' ? 'Partager' : 'Share'}
        </span>
      </Button>

      {/* Share Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white rounded-3xl max-w-sm w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-stone-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-stone-900">
                    {language === 'fr' ? 'Partager mon Ikigai' : 'Share my Ikigai'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 bg-stone-50">
              <div className="bg-white rounded-xl p-4 border border-stone-100">
                <p className="font-semibold text-stone-800 text-sm mb-1">{title}</p>
                <p className="text-stone-500 text-xs line-clamp-2">{description}</p>
              </div>
            </div>

            {/* Social buttons */}
            <div className="p-6 space-y-3">
              {/* Native share (mobile) */}
              {navigator.share && (
                <button
                  onClick={() => handleShare('native')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-[#FF6B6B]/10 to-[#4ECDC4]/10 hover:from-[#FF6B6B]/20 hover:to-[#4ECDC4]/20 transition-all"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-medium text-stone-800">
                    {language === 'fr' ? 'Partager...' : 'Share...'}
                  </span>
                </button>
              )}

              {/* Social platforms */}
              <div className="grid grid-cols-3 gap-3">
                {socialButtons.map((btn) => {
                  const Icon = btn.icon;
                  return (
                    <button
                      key={btn.id}
                      onClick={() => handleShare(btn.id)}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:scale-105"
                      style={{ backgroundColor: btn.bgColor }}
                      data-testid={`share-${btn.id}-btn`}
                    >
                      <Icon className="w-6 h-6" style={{ color: btn.color }} />
                      <span className="text-xs font-medium text-stone-600">{btn.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Copy link */}
              <button
                onClick={() => handleShare('copy')}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-stone-100 hover:bg-stone-200 transition-all"
                data-testid="share-copy-btn"
              >
                <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                  {copied ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <Link className="w-5 h-5 text-stone-600" />
                  )}
                </div>
                <span className="font-medium text-stone-800">
                  {copied 
                    ? (language === 'fr' ? 'Copié !' : 'Copied!')
                    : (language === 'fr' ? 'Copier le lien' : 'Copy link')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SocialShare;
